import React from 'react';
import { Link } from 'react-router-dom';
import { HeartIcon, MapPinIcon, BoltIcon, PlayIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { getAbsoluteImageUrl } from '../../lib/imageUtils';

interface AdCardProps {
  ad: {
    id: string;
    title: string;
    slug: string;
    price?: string;
    priceType: string;
    primaryImage?: string;
    location: string;
    city: string;
    condition: string;
    isFeatured?: boolean;
    isTrending?: boolean;
    publishedAt?: string;
    viewCount: number;
    favoriteCount: number;
    hasVideo?: boolean;
    videoUrl?: string;
    videoThumbnail?: string;
    user?: {
      id: string;
      username: string;
      displayName: string;
      profilePicture?: string;
    };
  };
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
  compact?: boolean;
}

const AdCard: React.FC<AdCardProps> = ({ ad, isFavorite, onToggleFavorite, compact = false }) => {
  const encodedSlug = encodeURIComponent(ad.slug);

  const formatPrice = (price: string | null | undefined) => {
    if (!price) return null;
    const numPrice = parseFloat(price);
    if (!isNaN(numPrice)) {
      return Math.round(numPrice).toLocaleString('en-IN');
    }
    return price;
  };

  const getPriceDisplay = () => {
    if (ad.priceType === 'free') return 'Free';
    if (ad.priceType === 'swap') return 'Swap';
    if (ad.price) return formatPrice(ad.price);
    return 'Contact';
  };

  const getConditionStyle = () => {
    if (!ad.condition) return { bg: '#9ca3af', text: 'white' };

    switch (ad.condition) {
      case 'new':
      case 'like_new':
        return { bg: 'var(--pw-success)', text: 'white' };
      case 'excellent':
        return { bg: '#4a84d9', text: 'white' };
      case 'good':
      case 'fair':
        return { bg: 'var(--pw-accent)', text: '#5c3d0d' };
      case 'poor':
        return { bg: 'var(--pw-danger)', text: 'white' };
      default:
        return { bg: '#9ca3af', text: 'white' };
    }
  };

  const conditionStyle = getConditionStyle();

  if (compact) {
    return (
      <Link to={`/ad/${encodedSlug}`} className="text-decoration-none d-block h-100">
        <div
          className="h-100 overflow-hidden"
          style={{
            background: 'var(--pw-surface)',
            border: '1px solid var(--pw-border)',
            borderRadius: 0,
            boxShadow: '0 4px 16px rgba(116, 67, 35, 0.05)',
            transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 12px 28px rgba(116, 67, 35, 0.1)';
            e.currentTarget.style.borderColor = '#f2d4c0';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 16px rgba(116, 67, 35, 0.05)';
            e.currentTarget.style.borderColor = 'var(--pw-border)';
          }}
        >
          {/* Image */}
          <div className="position-relative bg-light" style={{ aspectRatio: '1/1', overflow: 'hidden' }}>
            <img
              src={getAbsoluteImageUrl(ad.primaryImage) || '/placeholder-ad.png'}
              alt={ad.title}
              loading="lazy"
              style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s ease' }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.08)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            />

            {/* Video Badge */}
            {ad.hasVideo && (
              <div
                className="position-absolute d-flex align-items-center gap-1"
                style={{
                  top: 10,
                  left: 10,
                  padding: '5px 10px',
                  borderRadius: 10,
                  background: 'rgba(0,0,0,0.7)',
                  color: 'white',
                  fontSize: 10,
                  fontWeight: 700,
                  backdropFilter: 'blur(4px)',
                }}
              >
                <PlayIcon style={{ width: 10, height: 10 }} />
                VIDEO
              </div>
            )}

            {/* Featured Badge */}
            {ad.isFeatured && !ad.hasVideo && (
              <div
                className="position-absolute d-flex align-items-center gap-1"
                style={{
                  top: 10,
                  left: 10,
                  padding: '5px 10px',
                  borderRadius: 10,
                  background: 'linear-gradient(135deg, var(--pw-accent) 0%, #f5a623 100%)',
                  color: '#5c3d0d',
                  fontSize: 10,
                  fontWeight: 700,
                }}
              >
                <BoltIcon style={{ width: 10, height: 10 }} />
                FEATURED
              </div>
            )}

            {/* Sponsored Badge */}
            {ad.isFeatured && (
              <div
                className="position-absolute d-flex align-items-center gap-1"
                style={{
                  top: 10,
                  right: 10,
                  padding: '5px 10px',
                  borderRadius: 10,
                  background: 'linear-gradient(135deg, var(--pw-primary) 0%, var(--pw-primary-strong) 100%)',
                  color: 'white',
                  fontSize: 10,
                  fontWeight: 700,
                  boxShadow: '0 4px 12px rgba(232, 93, 76, 0.3)',
                }}
              >
                <BoltIcon style={{ width: 10, height: 10 }} />
                SPONSORED
              </div>
            )}

            {/* Condition Dot */}
            <div
              className="position-absolute"
              style={{
                bottom: 10,
                left: 10,
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: conditionStyle.bg,
                border: '2px solid white',
                boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
              }}
            />

            {/* Favorite Button */}
            {onToggleFavorite && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  onToggleFavorite();
                }}
                className="position-absolute d-flex align-items-center justify-content-center border-0"
                style={{
                  top: ad.isFeatured ? 'auto' : 10,
                  right: 10,
                  bottom: ad.isFeatured ? 10 : 'auto',
                  width: 32,
                  height: 32,
                  borderRadius: 10,
                  background: 'rgba(255,255,255,0.95)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  cursor: 'pointer',
                  transition: 'transform 0.15s ease',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
                onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
              >
                {isFavorite ? (
                  <HeartIconSolid style={{ width: 16, height: 16, color: 'var(--pw-danger)' }} />
                ) : (
                  <HeartIcon style={{ width: 16, height: 16, color: 'var(--pw-text-muted)' }} />
                )}
              </button>
            )}
          </div>

          {/* Content */}
          <div className="p-2">
            {/* Price */}
            <div
              className="fw-bold mb-1"
              style={{ fontSize: 12, color: 'var(--pw-primary-strong)' }}
            >
              {getPriceDisplay()}
            </div>

            {/* Title */}
            <h3
              className="fw-semibold mb-2"
              style={{
                fontSize: 14,
                color: 'var(--pw-text)',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                lineHeight: 1.4,
                minHeight: 40,
              }}
            >
              {ad.title}
            </h3>

            {/* Location */}
            <div
              className="d-flex align-items-center gap-1"
              style={{ fontSize: 12, color: 'var(--pw-text-muted)' }}
            >
              <MapPinIcon style={{ width: 12, height: 12, flexShrink: 0 }} />
              <span className="text-truncate">{ad.city || ad.location}</span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  // Full mode for desktop/grid
  return (
    <div
      className="h-100 overflow-hidden position-relative"
      style={{
        background: 'var(--pw-surface)',
        border: '1px solid var(--pw-border)',
        borderRadius: 0,
        boxShadow: '0 4px 18px rgba(116, 67, 35, 0.05)',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-6px)';
        e.currentTarget.style.boxShadow = '0 18px 40px rgba(116, 67, 35, 0.12)';
        e.currentTarget.style.borderColor = '#f2d4c0';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 4px 18px rgba(116, 67, 35, 0.05)';
        e.currentTarget.style.borderColor = 'var(--pw-border)';
      }}
    >
      {/* Image Container */}
      <Link
        to={`/ad/${encodedSlug}`}
        className="position-relative overflow-hidden d-block text-decoration-none"
        style={{ aspectRatio: '4/3' }}
      >
        <img
          src={getAbsoluteImageUrl(ad.primaryImage) || '/placeholder-ad.png'}
          alt={ad.title}
          loading="lazy"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.08)')}
          onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
        />

        {/* Video Play Overlay */}
        {ad.hasVideo && (
          <div
            className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
            style={{
              background: 'rgba(0,0,0,0.35)',
              opacity: 0,
              transition: 'opacity 0.3s ease',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = '0')}
          >
            <div
              className="d-flex align-items-center justify-content-center"
              style={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.95)',
                backdropFilter: 'blur(8px)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
              }}
            >
              <PlayIcon style={{ width: 28, height: 28, color: 'var(--pw-primary)', marginLeft: 4 }} />
            </div>
          </div>
        )}

        {/* Video Badge */}
        {ad.hasVideo && (
          <div
            className="position-absolute d-flex align-items-center gap-1"
            style={{
              top: 12,
              left: 12,
              padding: '6px 12px',
              borderRadius: 10,
              background: 'rgba(0,0,0,0.7)',
              color: 'white',
              fontSize: 11,
              fontWeight: 700,
              backdropFilter: 'blur(4px)',
            }}
          >
            <PlayIcon style={{ width: 12, height: 12 }} />
            VIDEO
          </div>
        )}

        {/* Badges */}
        <div className="position-absolute d-flex flex-column gap-2" style={{ top: 12, left: 12 }}>
          {!ad.hasVideo && ad.isFeatured && (
            <div
              className="d-flex align-items-center gap-1"
              style={{
                padding: '6px 12px',
                borderRadius: 10,
                background: 'linear-gradient(135deg, var(--pw-accent) 0%, #f5a623 100%)',
                color: '#5c3d0d',
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              <BoltIcon style={{ width: 12, height: 12 }} />
              FEATURED
            </div>
          )}
          {ad.isTrending && (
            <div
              className="d-flex align-items-center gap-1"
              style={{
                padding: '6px 12px',
                borderRadius: 10,
                background: 'var(--pw-primary)',
                color: 'white',
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              🔥 TRENDING
            </div>
          )}
        </div>

        {/* Sponsored Badge */}
        {ad.isFeatured && (
          <div
            className="position-absolute d-flex align-items-center gap-1"
            style={{
              top: 12,
              right: 56,
              padding: '6px 12px',
              borderRadius: 10,
              background: 'linear-gradient(135deg, var(--pw-primary) 0%, var(--pw-primary-strong) 100%)',
              color: 'white',
              fontSize: 11,
              fontWeight: 700,
              boxShadow: '0 4px 12px rgba(232, 93, 76, 0.3)',
            }}
          >
            <BoltIcon style={{ width: 12, height: 12 }} />
            SPONSORED
          </div>
        )}

        {/* Favorite Button */}
        {onToggleFavorite && (
          <button
            onClick={(e) => {
              e.preventDefault();
              onToggleFavorite();
            }}
            className="position-absolute d-flex align-items-center justify-content-center border-0"
            style={{
              top: 12,
              right: 12,
              width: 38,
              height: 38,
              borderRadius: 12,
              background: 'rgba(255,255,255,0.95)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              cursor: 'pointer',
              transition: 'transform 0.2s ease',
              zIndex: 2,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.12)')}
            onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
          >
            {isFavorite ? (
              <HeartIconSolid style={{ width: 20, height: 20, color: 'var(--pw-danger)' }} />
            ) : (
              <HeartIcon style={{ width: 20, height: 20, color: 'var(--pw-text-muted)' }} />
            )}
          </button>
        )}

        {/* Condition Badge */}
        {ad.condition && (
          <div
            className="position-absolute d-flex align-items-center"
            style={{
              bottom: 12,
              left: 12,
              padding: '5px 10px',
              borderRadius: 8,
              background: conditionStyle.bg,
              color: conditionStyle.text,
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.02em',
            }}
          >
            {ad.condition.replace('_', ' ')}
          </div>
        )}
      </Link>

      {/* Content */}
      <div className="p-2">
        {/* Price */}
        <div className="d-flex align-items-center justify-content-between mb-2">
          <span
            className="fw-bold"
            style={{ fontSize: 13, color: 'var(--pw-primary-strong)' }}
          >
            {getPriceDisplay()}
          </span>
          {ad.priceType === 'negotiable' && (
            <span style={{ fontSize: 12, color: 'var(--pw-text-muted)', fontWeight: 500 }}>
              Negotiable
            </span>
          )}
        </div>

        {/* Title */}
        <Link to={`/ad/${encodedSlug}`} className="text-decoration-none d-block mb-3">
          <h3
            className="fw-semibold text-primary-hover"
            style={{
              fontSize: 15,
              color: 'var(--pw-text)',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              lineHeight: 1.45,
              minHeight: 44,
              transition: 'color 0.2s ease',
            }}
          >
            {ad.title}
          </h3>
        </Link>

        {/* Location */}
        <div
          className="d-flex align-items-center gap-1 mb-3"
          style={{ fontSize: 13, color: 'var(--pw-text-muted)' }}
        >
          <MapPinIcon style={{ width: 14, height: 14, flexShrink: 0 }} />
          <span className="text-truncate">{ad.city || ad.location}</span>
        </div>

      </div>
    </div>
  );
};

export default AdCard;
